import React, { useState, useEffect } from 'react';
import { AuthForm } from '@/components/auth/AuthForm';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { getAndClearDeepLink } from '@/utils/deepLinking';
// Removed: import { checkBracketStatus } from '@/utils/auth-utils'; // Assuming it's not needed
import { useToast } from "@/components/ui/use-toast";

interface AuthProps {
  tradingOnly?: boolean;
}

const Auth = ({ tradingOnly = false }: AuthProps) => {
  const [formType, setFormType] = useState<'login' | 'signup'>('login');
  const navigate = useNavigate();
  const location = useLocation();
  // --- CHANGE THIS LINE ---
  const from = location.state?.from?.pathname || '/dashboard'; // Default to /dashboard
  // --- END CHANGE ---
  const { toast } = useToast();

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // User is already authenticated, so we can redirect them

          // If it's trading-only mode, verify they have appropriate access
          if (tradingOnly) {
            // This logic remains the same, potentially redirecting elsewhere if needed
            const { data: roles } = await supabase
              .from('user_roles')
              .select('role')
              .eq('user_id', session.user.id);

            if (!roles || !roles.length) {
              console.log("User doesn't have trading access, redirecting to general area");
              // Maybe redirect to a specific 'access needed' page or just the dashboard
              navigate('/dashboard'); // Example: redirect to dashboard anyway
              return;
            }
          }

          // Successful authentication check - redirect to intended destination
          console.log("User already authenticated, redirecting to:", from);
          navigate(from); // 'from' will now default to '/dashboard'
        }
        // If no session, do nothing and let the Auth form display normally
      } catch (error) {
        console.error("Error checking authentication:", error);
        // Don't show an error to the user, just log it for debugging
      }
    };

    checkAuth();
  }, [navigate, from]); // Dependencies remain the same

  const toggleFormType = () => {
    setFormType(prev => prev === 'login' ? 'signup' : 'login');
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-slate-900">
      <div className="w-full flex-1 flex flex-col md:flex-row">
        {/* Branding/Left Section (No changes needed here) */}
        <div className="w-full md:w-1/2 bg-gradient-to-br from-slate-900 to-slate-800 p-8 flex items-center justify-center">
          {/* ... existing branding content ... */}
           <div className="max-w-md w-full">
            <Link to="/" className="block mb-8">
              <img
                src="/lovable-uploads/1091d23b-2cf4-40d4-b2ec-3d052c262c74.png"
                alt="Stewie Trader"
                className="h-28 mx-auto md:mx-0"
              />
            </Link>

            <div className="hidden md:block">
              <h1 className="text-3xl font-bold text-white mb-4">
                Smarter Trades. <span className="text-blue-500">Sharper Decisions.</span>
              </h1>
              <p className="text-gray-400 mb-6">
                Join thousands of traders using advanced algorithms to identify market opportunities.
              </p>

              <div className="space-y-4 mt-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-xs text-white">✓</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-white font-medium">Institutional-grade AI</h3>
                    <p className="text-gray-400 text-sm">Advanced models that process millions of data points</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-xs text-white">✓</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-white font-medium">Real-time analysis</h3>
                    <p className="text-gray-400 text-sm">Millisecond processing of market changes</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-xs text-white">✓</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-white font-medium">Risk management</h3>
                    <p className="text-gray-400 text-sm">Sophisticated controls to protect your capital</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Auth Form/Right Section (No changes needed here) */}
        <div className="w-full md:w-1/2 bg-slate-950 flex items-center justify-center p-8">
          <div className="max-w-md w-full">
            <AuthForm
              formType={formType}
              toggleFormType={toggleFormType}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
